/**
 * InspectorDrawer — unified right-side panel showing the full pipeline
 * for a single item (wiring + field consumption) or bundle overview.
 *
 * No tabs — one click, all the info.
 */

import { RawJsonPanel } from './RawJsonPanel';
import { WiringTab } from './WiringTab';
import { debugTheme, truncateId } from './debugTheme';
import type { ViewItem, ViewBundle } from '../../../../../src/shared/view-system/types';

const t = debugTheme.dark;

export interface InspectorDrawerProps {
  inspectedItem: ViewItem | null;
  inspectedBundle: ViewBundle | null;
  wiringTarget: string | null;
  onClose: () => void;
  onInspectItem: (item: ViewItem) => void;
}

// Bundle view (no wiring available — show bundle overview)

function BundleView({
  bundle,
  onInspectItem,
}: {
  bundle: ViewBundle;
  onInspectItem: (item: ViewItem) => void;
}) {
  return (
    <>
      <div className={`flex-shrink-0 px-4 py-3 ${t.border} border-b ${t.bgAlt}`}>
        <div className={`text-[11px] font-mono ${t.text} space-y-1`}>
          <div className="flex gap-3">
            <span>
              <span className={t.textMuted}>kind:</span>{' '}
              <span className={t.textAccent2}>{bundle.kind}</span>
            </span>
            <span>
              <span className={t.textMuted}>correlationId:</span>{' '}
              <span className={t.textBright}>{truncateId(bundle.correlationId)}</span>
            </span>
          </div>
          <div className="flex gap-3">
            <span>
              <span className={t.textMuted}>total:</span> {bundle.stats.total}
            </span>
            <span>
              <span className={t.textMuted}>pending:</span> {bundle.stats.pending}
            </span>
            <span>
              <span className={t.textMuted}>actioned:</span> {bundle.stats.actioned}
            </span>
            <span>
              <span className={t.textMuted}>unread:</span> {bundle.stats.unread}
            </span>
          </div>
          <div>
            <span className={t.textMuted}>bulkActions:</span> {bundle.bulkActions?.length ?? 0}
          </div>

          {/* Contained items list */}
          <div className={`mt-2 ${t.border} border-t pt-2`}>
            <div className={`${t.textMuted} mb-1`}>Items ({bundle.items.length}):</div>
            <div className="space-y-0.5 max-h-[150px] overflow-y-auto">
              {bundle.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onInspectItem(item)}
                  className={`w-full text-left px-2 py-1 rounded hover:bg-nightshade-700 ${t.text} hover:text-white transition-colors`}
                >
                  <span className={t.textAccent}>{item.artifactCard}</span>{' '}
                  <span className={t.textMuted}>{truncateId(item.id, 8)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Raw JSON */}
      <div className="flex-1 overflow-hidden">
        <RawJsonPanel data={bundle} />
      </div>
    </>
  );
}

// Main drawer

export function InspectorDrawer({
  inspectedItem,
  inspectedBundle,
  wiringTarget,
  onClose,
  onInspectItem,
}: InspectorDrawerProps) {
  const isOpen = inspectedItem !== null || inspectedBundle !== null || wiringTarget !== null;

  return (
    <div
      className={`fixed top-0 right-0 h-full w-[520px] z-[9998] ${t.bg} ${t.border} border-l shadow-2xl flex flex-col transition-transform duration-200 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {/* Top bar */}
      <div className={`flex items-center justify-between px-4 py-2 ${t.border} border-b ${t.bgAlt} flex-shrink-0`}>
        <span className={`text-[11px] font-mono font-semibold ${t.textAccent}`}>
          {inspectedBundle ? 'Bundle' : 'Inspector'}
        </span>
        <button
          type="button"
          onClick={onClose}
          className={`ml-3 flex-shrink-0 ${t.textMuted} hover:text-white transition-colors text-lg leading-none`}
          title="Close inspector"
        >
          &times;
        </button>
      </div>

      {/* Bundle view */}
      {inspectedBundle && (
        <BundleView bundle={inspectedBundle} onInspectItem={onInspectItem} />
      )}

      {/* Item view: wiring + field map */}
      {!inspectedBundle && wiringTarget && (
        <WiringTab wiringTarget={wiringTarget} />
      )}
    </div>
  );
}
